import React from 'react';
import {
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useIntl } from 'react-intl';

import {
  IS_MERCH_COUNT_IN_TOT,
  MAX_CART_ITEM_AMOUNT,
  getProductDescription,
  getProductName,
  getTotalProductNum,
} from '../utils';

import translations from 'utils/translations';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import { useState } from 'react';
import { useEffect } from 'react';
import { RedLinkButton } from '../style';
import { isDiscountTicket, Roles } from 'utils/constants';

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

const ModalContent = ({ locale, onAccept, loggedUser }) => {
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const intl = useIntl();
  const i18nRecap = translations.TicketsV2(intl).Recap;
  const i18nCommon = translations.TicketsV2(intl).Common;
  const isOperator = loggedUser.type === Roles.OPERATOR;
  const { fetchedProducts, productsInCart, merchInCart } = useTicketsStore();
  const [ticketsQuantity, setTicketsQuantity] = useState({});
  const [expandedCardsIdx, setExpandedCardsIdx] = useState([]);
  const [temporaryCartCount, setTemporaryCartCount] = useState(0);

  useEffect(() => {
    const totalInCart = IS_MERCH_COUNT_IN_TOT
      ? getTotalProductNum(productsInCart.concat(merchInCart))
      : getTotalProductNum(productsInCart);
    setTemporaryCartCount(totalInCart);
  }, [productsInCart, merchInCart]);

  useEffect(() => {
    if (fetchedProducts?.length) {
      const quantities = {};
      fetchedProducts.map((t) => {
        quantities[t.id] = 0;
      });
      setTicketsQuantity(quantities);
    }
  }, [fetchedProducts]);

  const handleAdd = (id) => {
    if (temporaryCartCount < MAX_CART_ITEM_AMOUNT) {
      setTemporaryCartCount((prev) => prev + 1);
      onAddClick(id);
    }
  };

  const onAddClick = (id) => {
    if (ticketsQuantity[id] < 99)
      setTicketsQuantity((prev) => ({
        ...prev,
        [id]: prev[id] + 1,
      }));
  };

  const onRemoveClick = (id) => {
    if (temporaryCartCount > 0) setTemporaryCartCount((prev) => prev - 1);
    setTicketsQuantity((prev) => ({ ...prev, [id]: prev[id] - 1 }));
  };

  const toggleDetails = (idx) => {
    const isExpanded = expandedCardsIdx.find((index) => index === idx);
    if (isExpanded) {
      setExpandedCardsIdx(expandedCardsIdx.filter((index) => index !== idx));
    } else {
      setExpandedCardsIdx((prev) => [...prev, idx]);
    }
  };

  return (
    <>
      <Stack sx={{ mt: '25px' }} direction={'column'} gap={'25px'}>
        {fetchedProducts
          .filter((product) => !isDiscountTicket(product))
          .map((product, idx) => {
            const isLegacyTicket =
              product.capacity === 6 && !isDiscountTicket(product);
            return (
              <Stack
                key={product.id}
                sx={{
                  p: '25px 20px',
                  bgcolor: 'white',
                  border: `2px solid ${
                    ticketsQuantity[product.id] === 0
                      ? theme.palette.grey[200]
                      : theme.palette.primary.main
                  }`,
                }}
              >
                {isLgDown ? (
                  <>
                    {isOperator && !isLegacyTicket ? (
                      <div
                        style={{
                          backgroundColor: theme.palette.grey[200],
                          color: 'black',
                          borderRadius: '25px',
                          width: 'fit-content',
                          display: 'flex',
                          padding: '5px 10px',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '16px',
                        }}
                      >
                        <Typography
                          variant="xs-bold"
                          textAlign="center"
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {i18nCommon.novelty} {new Date().getFullYear()}
                        </Typography>
                      </div>
                    ) : null}
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      alignItems={'flex-start'}
                      sx={{
                        mb: expandedCardsIdx.find((index) => index === idx + 1)
                          ? '15px'
                          : '30px',
                      }}
                    >
                      <Typography variant="m-bold" sx={{ mb: '5px' }}>
                        {getProductName(product, locale)}
                      </Typography>
                      <RedLinkButton
                        onClick={() => toggleDetails(idx + 1)}
                        msg={i18nCommon.details}
                        icon={
                          expandedCardsIdx.find(
                            (index) => index === idx + 1
                          ) ? (
                            <ExpandLessIcon color="primary" />
                          ) : (
                            <ExpandMoreIcon color="primary" />
                          )
                        }
                      />
                    </Stack>
                  </>
                ) : (
                  <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'flex-start'}
                  >
                    {isOperator && !isLegacyTicket ? (
                      <div
                        style={{
                          backgroundColor: theme.palette.grey[200],
                          color: 'black',
                          borderRadius: '25px',
                          width: 'fit-content',
                          display: 'flex',
                          padding: '5px 10px',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        <Typography
                          variant="xs-bold"
                          textAlign="center"
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {i18nCommon.novelty} {new Date().getFullYear()}
                        </Typography>
                      </div>
                    ) : null}
                    <Typography variant="m-bold" sx={{ mb: '5px' }}>
                      {getProductName(product, locale)}
                    </Typography>
                  </Stack>
                )}
                {!isLgDown ||
                expandedCardsIdx.find((index) => index === idx + 1) ? (
                  <Typography variant="body2" sx={{ mb: '15px' }}>
                    {getProductDescription(product, locale)}
                  </Typography>
                ) : null}
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Typography variant="body2-bold" color={'grey.800'}>
                    {product?.current_price
                      ? currencyFormatter.format(product?.current_price)
                      : null}
                  </Typography>
                  <Stack direction={'row'}>
                    <Button
                      disabled={ticketsQuantity[product.id] === 0}
                      variant="contained"
                      sx={{
                        width: '45px',
                        height: '45px',
                        padding: 0,
                        minWidth: 0,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        onRemoveClick(product.id);
                      }}
                    >
                      <RemoveIcon fontSize="20px" />
                    </Button>
                    <Stack
                      alignItems={'center'}
                      justifyContent={'center'}
                      sx={{ width: '40px' }}
                    >
                      <Typography variant="body2-bold">
                        {ticketsQuantity[product.id]}
                      </Typography>
                    </Stack>
                    <Button
                      disabled={
                        temporaryCartCount === MAX_CART_ITEM_AMOUNT ||
                        ticketsQuantity[product.id] === 99
                      }
                      variant="contained"
                      sx={{
                        width: '45px',
                        height: '45px',
                        padding: 0,
                        minWidth: 0,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleAdd(product.id);
                      }}
                    >
                      <AddIcon fontSize="20px" />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
        {Object.values(ticketsQuantity).some((quantity) => quantity !== 0) ? (
          <Stack
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'center'}
            marginTop={'25px'}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => onAccept(ticketsQuantity)}
            >
              {i18nRecap.assignNew}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </>
  );
};
export default ModalContent;
